import { WorkflowConstants } from "lookup/WorkflowConstants";
import UsabilityContext from "lookup/UsabilityContext";
import { DEFAULT_RAW_FAX_PROGRAM_CODE } from "lookup/RawConstants";
import { getRawListRedirectPath } from "./faxDispositionUrlUtils";
import {
  showWhenDeclinedDisposition,
  showWhenNotRawConfirmed,
} from "./faxDispositionActionVisibility";

export const FAX_DISPOSITION_PROGRAM_CODES = {
  SWITCH: "STD",
  DMR: "DMR",
};

export const DISPOSITION_FIELD_COMPONENTS = {
  SWITCH: "SwitchDispositionFields",
  DMR: "DmrDispositionFields",
};

export const FAX_DISPOSITION_CONFIG = {
  [FAX_DISPOSITION_PROGRAM_CODES.SWITCH]: {
    dispositionFieldComponent: DISPOSITION_FIELD_COMPONENTS.SWITCH,
    elementIdPrefix: "switchdisposition",
    detectedDispositionContainerId: "switchDispositionContainer",
    header: {
      getListLink: (faxProgramCode, contextIsRaw, listQueryParams = "") => ({
        href: contextIsRaw
          ? getRawListRedirectPath(listQueryParams, faxProgramCode)
          : "/switch/list",
        text: contextIsRaw ? "Switch Responses" : "Switch List",
      }),
      getActiveBreadcrumbText: (rawDetails, contextIsRaw) =>
        contextIsRaw ? `Switch ${rawDetails.globalSwitchId}` : "Disposition",
      getSwimBreadcrumb: (rawDetails) => ({
        switchHref: `/switch/${rawDetails.wfItemId}`,
        switchLabel: `Switch ${rawDetails.globalSwitchId}`,
      }),
      dispositionFor: {
        label: "Switch",
        getDisplayId: (rawDetails) => rawDetails.globalSwitchId,
        getLinkHref: (rawDetails, rawId) =>
          `/raw/${rawId}/wf/${rawDetails.wfItemId}`,
        getSwimDisplayId: (rawDetails) => rawDetails.globalSwitchId,
        showControlledBadge: true,
      },
      showSwimForwardBadge: true,
    },
    notes: {
      label: "Notes",
      placeholder: "Notes or comments about this switch",
      helpText: "Notes or comments about this switch (optional)",
      controlId: "switchdisposition-form-notes",
    },
    unlinkDeclinedFax: {
      notesPlaceholder: "Notes or comments about this switch",
      notesControlId: "switchdisposition-form-notes",
      getWarningLines: (rawDetails) => [
        `You are about to unlink Confirmed disposition DECLINED for Switch # ${rawDetails.globalSwitchId}.`,
        "This will return the switch to switch state: Awaiting Response.",
        "Are you sure you want to unlink the fax?",
      ],
    },
    actions: {
      buttonMinHeight: "38px",
      actionVisibility: {
        unlinkDeclinedFax: showWhenDeclinedDisposition,
      },
      raw: {
        rows: [
          {
            columns: [
              { width: 6, actions: ["unclaim"] },
              { width: 6, actions: ["createActivity"] },
            ],
          },
          {
            columns: [{ width: 12, actions: ["saveDisposition"] }],
          },
          {
            columns: [{ width: 12, actions: ["submitForReview"] }],
          },
          {
            columns: [{ width: 12, actions: ["changeTemplate"] }],
          },
          {
            columns: [{ width: 12, actions: ["pushToPharmacy"] },],
          },
          {
            columns: [{ width: 12, actions: ["unlinkDeclinedFax"] }],
          },
        ],
      },
      swim: {
        showOwnerAlert: true,
        rows: [
          {
            columns: [{ width: 12, actions: ["saveDisposition"] }],
          },
          {
            columns: [
              { width: 6, actions: ["unlinkDeclinedFax"] },
              { width: 6, actions: ["pushToPharmacy"] },
            ],
          },
        ],
      },
    },
    getPdfUrl: (rawDetails) =>
      rawDetails.wfItemStatus === WorkflowConstants.DELIVERED_PRESCRIBER_FAX
        ? `/pdf/${rawDetails.customerId}/prescriber-inbound-or-generated-redacted?switch_id=${rawDetails.switchId}`
        : `/pdf/prescriber-inbound-redacted-fax/${rawDetails.wfItemId}`,
  },
  [FAX_DISPOSITION_PROGRAM_CODES.DMR]: {
    rawOnly: true,
    dispositionFieldComponent: DISPOSITION_FIELD_COMPONENTS.DMR,
    header: {
      getListLink: (faxProgramCode, _contextIsRaw, listQueryParams = "") => ({
        href: getRawListRedirectPath(listQueryParams, faxProgramCode),
        text: "DMR Responses",
      }),
      getActiveBreadcrumbText: (rawDetails) =>
        rawDetails.globalSwitchId
          ? `Ref ID ${rawDetails.globalSwitchId}`
          : `Ref ID Not Found`,
      dispositionFor: {
        label: "Ref ID",
        getDisplayId: (rawDetails) => rawDetails.globalSwitchId || "Not Found",
        getLinkHref: null,
        showControlledBadge: false,
      },
      showSwimForwardBadge: false,
    },
    dispositionFields: {
      label: "Disposition",
      checkboxes: [
        {
          name: "patientNoLongerUnderPrescriberCare",
          label: "Patient is no longer under this prescriber's care",
          value: "PATIENT_NO_LONGER_UNDER_PRESCRIBER_CARE",
        },
        {
          name: "prescriberNoLongerAtPractice",
          label: "Prescriber no longer works at this practice",
          value: "PRESCRIBER_NO_LONGER_AT_PRACTICE",
        },
        {
          name: "isContinueMedication",
          label: "Medication is still prescribed for this patient",
          value: "IS_CONTINUE_MEDICATION",
        },
        {
          name: "isContinuationKeywordFound",
          label: "Is Continuation Keyword Found",
          value: "IS_CONTINUATION_KEYWORD_FOUND",
        },
        {
          name: "deprescriptionConfirmed",
          label: "Deprescription Confirmed",
          value: "DEPRESCRIPTION_CONFIRMED",
        },
      ],
    },
    notes: {
      label: "Notes",
      placeholder: "Notes or comments about this DMR response",
      helpText: "Notes or comments about this DMR response (optional)",
      isVisible: showWhenNotRawConfirmed,
    },
    unlinkDeclinedFax: {
      notesPlaceholder: "Notes or comments about this DMR response",
      notesControlId: "faxdisposition-form-notes",
      getWarningLines: (rawDetails) => {
        const refId = rawDetails.globalSwitchId || "Not Found";
        return [
          `You are about to unlink the fax for DMR response Ref ID ${refId}.`,
          "This will return the response to Awaiting Response.",
          "Are you sure you want to unlink the fax?",
        ];
      },
    },
    actions: {
      buttonMinHeight: "38px",
      actionVisibility: {
        unclaim: showWhenNotRawConfirmed,
        unlinkDeclinedFax: showWhenNotRawConfirmed,
        saveDisposition: showWhenNotRawConfirmed,
        submitForReview: showWhenNotRawConfirmed,
      },
      raw: {
        rows: [
          {
            columns: [
              { width: 6, actions: ["unclaim"] },
              { width: 6, actions: ["unlinkDeclinedFax"] },
            ],
          },
          {
            columns: [{ width: 12, actions: ["saveDisposition"] }],
          },
          {
            columns: [{ width: 12, actions: ["submitForReview"] }],
          },
        ],
      },
    },
    getPdfUrl: (rawDetails) => {
      return `/pdf/prescriber-inbound-redacted-fax-by-fax-info/${encodeURIComponent(rawDetails.faxInfoUuid)}`;
    },
  },
};

export const getFaxDispositionConfig = (
  faxProgramCode = DEFAULT_RAW_FAX_PROGRAM_CODE
) => {
  const programKey = faxProgramCode?.trim() || DEFAULT_RAW_FAX_PROGRAM_CODE;
  return (
    FAX_DISPOSITION_CONFIG[programKey] ||
    FAX_DISPOSITION_CONFIG[DEFAULT_RAW_FAX_PROGRAM_CODE]
  );
};

export const getUnlinkDeclinedFaxConfig = (
  faxProgramCode = DEFAULT_RAW_FAX_PROGRAM_CODE
) => {
  const config = getFaxDispositionConfig(faxProgramCode);
  return (
    config.unlinkDeclinedFax ||
    FAX_DISPOSITION_CONFIG[FAX_DISPOSITION_PROGRAM_CODES.SWITCH].unlinkDeclinedFax
  );
};

export const getActionsLayoutForContext = (config, contextIsRaw) => {
  const actionsConfig = config?.actions;
  if (!actionsConfig) {
    return { rows: [], buttonMinHeight: "38px", showOwnerAlert: false };
  }

  const useRawLayout = contextIsRaw || config.rawOnly || !actionsConfig.swim;
  const contextLayout = useRawLayout ? actionsConfig.raw : actionsConfig.swim;

  return {
    rows: contextLayout?.rows ?? [],
    buttonMinHeight: actionsConfig.buttonMinHeight ?? "38px",
    showOwnerAlert: contextLayout?.showOwnerAlert ?? false,
    actionVisibility: actionsConfig.actionVisibility ?? {},
  };
};

export const resolveFaxDispositionContext = (location, faxProgramCode) => {
  if (faxProgramCode === FAX_DISPOSITION_PROGRAM_CODES.DMR) {
    return UsabilityContext.RAW;
  }

  const contextIsRaw =
    location.pathname.startsWith("/raw") ||
    (location.state && location.state.context === UsabilityContext.RAW);

  return contextIsRaw ? UsabilityContext.RAW : UsabilityContext.SWIM;
};
